import { extractKnovEmbedTarget } from 'components/shared/knovLinkTargets'

export const knovEmbedTargetKey = target => {
    if (!target?.type || !target?.id) return null
    return `${target.type}:${target.id}`
}

export const getKnovEmbedTargets = (knovEmbedLinks = []) => {
    return (knovEmbedLinks || []).map(extractKnovEmbedTarget).filter(Boolean)
}

export const getKnovEmbedTargetKeys = (knovEmbedLinks = []) => {
    return getKnovEmbedTargets(knovEmbedLinks).map(knovEmbedTargetKey).filter(Boolean).map(String)
}

export const resolveKnovEmbedLink = async (
    url,
    { fetchQuest = null, fetchAnswer = null, fetchAnonymousShareAnswer = null } = {},
) => {
    const target = extractKnovEmbedTarget(url)
    if (!target) return null

    if (target.type === 'anonymous_share') {
        if (typeof fetchAnonymousShareAnswer !== 'function') return null
        return fetchAnonymousShareAnswer(target.id)
    }

    if (target.type === 'answer') {
        if (typeof fetchAnswer !== 'function') return null
        return fetchAnswer(target.id)
    }

    if (target.type === 'share' && typeof fetchAnswer === 'function') {
        const answer = await fetchAnswer(target.id)
        if (answer) return answer
    }

    if (typeof fetchQuest !== 'function') return null
    const quest = await fetchQuest(target.id)
    return quest?.parent || null
}

export const resolveKnovEmbedLinks = async (
    knovEmbedLinks = [],
    { fetchQuest = null, fetchAnswer = null, fetchAnonymousShareAnswer = null } = {},
) => {
    return Promise.all(
        (knovEmbedLinks || []).map(url =>
            resolveKnovEmbedLink(url, {
                fetchQuest,
                fetchAnswer,
                fetchAnonymousShareAnswer,
            }),
        ),
    )
}

const answerPathQuestKey = answer => {
    if (typeof answer?.path !== 'string') return null
    return extractKnovEmbedTarget(answer.path)?.questId || null
}

export const answerMatchesKnovEmbedTarget = (answer, target) => {
    if (!answer || !target?.type || !target?.id) return false

    if (target.type === 'anonymous_share') {
        const shareLinkIds = Array.isArray(answer.anonymous_share_link_ids)
            ? answer.anonymous_share_link_ids
            : []
        return shareLinkIds.some(id => String(id) === String(target.id))
    }

    if (target.type === 'answer' || target.type === 'share') {
        if (String(answer.id || '') === String(target.id)) return true
        if (target.type === 'answer') return false
    }

    const childQuestIds = Array.isArray(answer.child_quests)
        ? answer.child_quests.map(quest => quest?.id || quest?.key).filter(Boolean)
        : []
    const candidateQuestIds = [
        answer.downtree_quest_id,
        answer.child_quest_id,
        answerPathQuestKey(answer),
        ...childQuestIds,
    ]

    return candidateQuestIds.filter(Boolean).some(id => String(id) === String(target.id))
}

export const answerMatchesAnyKnovEmbedTarget = (answer, targets = []) => {
    return (targets || []).some(target => answerMatchesKnovEmbedTarget(answer, target))
}

export const filterEmbedsForKnovEmbedLinks = (
    embeds = [],
    knovEmbedLinks = [],
    resolveEmbedAnswer = embed => embed,
) => {
    const targets = getKnovEmbedTargets(knovEmbedLinks)
    return (embeds || []).filter(embed => {
        if (answerMatchesAnyKnovEmbedTarget(embed, targets)) return true

        const resolvedEmbed = resolveEmbedAnswer(embed)
        return resolvedEmbed !== embed && answerMatchesAnyKnovEmbedTarget(resolvedEmbed, targets)
    })
}

// The server swaps embeds the viewer cannot see for this placeholder (Perms
// PRIVATE_EMBED_PLACEHOLDER). It carries no id to match a link against.
const PRIVATE_EMBED_ID = '__PRIVATE__'

// Keep the embeds that match a visible link, plus one private placeholder for
// each visible link no other embed covers, so PostEmbed can show it as private.
export const filterEmbedsForVisibleTargets = (
    embeds = [],
    targets = [],
    resolveEmbedAnswer = embed => embed,
) => {
    const matchedEmbeds = (embeds || []).filter(embed =>
        answerMatchesAnyKnovEmbedTarget(resolveEmbedAnswer(embed), targets),
    )
    let privateSlots = (targets || []).filter(
        target =>
            !matchedEmbeds.some(embed =>
                answerMatchesKnovEmbedTarget(resolveEmbedAnswer(embed), target),
            ),
    ).length

    return (embeds || []).filter(embed => {
        if (matchedEmbeds.includes(embed)) return true
        if (embed?.id !== PRIVATE_EMBED_ID || privateSlots === 0) return false
        privateSlots -= 1
        return true
    })
}

export const getMatchedKnovEmbedTargetKeys = (
    embeds = [],
    targets = [],
    resolveEmbedAnswer = embed => embed,
) => {
    return (targets || [])
        .filter(target =>
            (embeds || []).some(embed =>
                answerMatchesKnovEmbedTarget(resolveEmbedAnswer(embed), target),
            ),
        )
        .map(knovEmbedTargetKey)
        .filter(Boolean)
        .map(String)
}
